// Client auth context — plan 23.
//
// Plan 22 flipped auth to an httpOnly cookie + /api/me probe. Plan 23
// backs the user state with React Query's `['me']` key so every consumer
// reads from the same cache. The context itself now only carries the
// modal state + the credentialed action helpers.
//
// Shape:
//   { user, isAuthenticated, login, register, logout, openAuthModal,
//     closeAuthModal, pendingAction, clearPendingAction }
//
// The deprecated `token: null` / `setUser` / `loading` / `isAuthModalOpen`
// fields are preserved ONLY to soften the migration for any lingering
// consumer. Every field except `isAuthModalOpen` will be removed once
// the grep tripwire in plan 23 settles; `isAuthModalOpen` is load-bearing
// for `AuthModal`.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UserData } from '../../shared/types';
import { navigate } from '../../app/router';
import { prefetchPostLoginChunks } from '../../app/prefetch';
import { authKeys } from '../../shared/api/queryKeys';
import { invalidate } from '../../shared/api/invalidations';
import {
  fetchMe,
  login as loginRequest,
  logout as logoutRequest,
  registerUser as registerRequest,
  type LoginCredentials,
  type RegisterPayload,
} from './authApi';
import {
  setVisitorClassification,
  trackLogin,
  trackSignup,
  type UserType,
} from '../../shared/analytics/track';

export type { LoginCredentials, RegisterPayload } from './authApi';

interface AuthContextValue {
  /** Current authenticated user or null. Sourced from React Query `['me']`. */
  user: UserData | null;
  /** True once `['me']` has resolved to a non-null user. */
  isAuthenticated: boolean;
  /**
   * @deprecated Plan 22: the JWT is no longer accessible from client code.
   * The cookie flows automatically via `credentials: 'include'` in
   * `safeFetch`. Always `null`; field retained only for callers still in
   * the process of dropping `Authorization: Bearer` headers.
   */
  token: null;
  /**
   * @deprecated Plan 23 migrated the initial `/api/me` probe to React Query.
   * Prefer reading `useCurrentUser().isLoading`.
   */
  loading: boolean;
  /** Sign in; server sets the httpOnly cookie. */
  login: (credentials: LoginCredentials) => Promise<UserData>;
  /** Clear server + local session. */
  logout: () => Promise<void>;
  /** Register + auto-login. */
  register: (payload: RegisterPayload) => Promise<UserData>;
  /**
   * @deprecated Plan 23: profile edits go through `useUpdateProfile()`.
   * Retained as a no-op fallback for any caller still in flight; reading
   * it in a new component is a code smell.
   */
  setUser: (u: UserData | null) => void;
  /** Opens the auth modal; optionally records an action to run after login. */
  openAuthModal: (opts?: { pendingAction?: () => void }) => void;
  /** True while the auth modal should be visible. */
  isAuthModalOpen: boolean;
  closeAuthModal: () => void;
  /** Optional action scheduled at `openAuthModal` time. */
  pendingAction: (() => void) | null;
  /** Drop any queued pending action without running it. */
  clearPendingAction: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const qc = useQueryClient();

  // The `/api/me` probe now lives in React Query. StrictMode double-mount
  // is harmless because the query dedupes by key.
  const meQuery = useQuery({
    queryKey: authKeys.me(),
    queryFn: fetchMe,
    retry: 0,
    // `fetchMe` intentionally resolves to `null` on a 401 so the landing
    // page renders without an error state.
  });

  const user = meQuery.data ?? null;
  const loading = meQuery.isPending;

  const [isAuthModalOpen, setAuthModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  // Surface non-401 probe failures once. Don't block the UI — the landing
  // page renders in anonymous mode on failure.
  useEffect(() => {
    if (meQuery.error) {
      console.warn('Auth probe failed:', meQuery.error);
    }
  }, [meQuery.error]);

  // Classify the visitor for GA + Meta — fires on first probe resolution
  // and on every subsequent user-state change. The provider is no-op
  // until the analytics modules have been initialised in main.tsx.
  useEffect(() => {
    if (loading) return;
    const purchaseCount = user?.purchase_count ?? 0;
    let userType: UserType;
    if (!user) userType = 'anon';
    else if (user.role === 'admin') userType = 'admin';
    else if (purchaseCount === 0) userType = 'authenticated';
    else if (purchaseCount === 1) userType = 'first_customer';
    else userType = 'repeat_customer';
    setVisitorClassification(userType, user?.id ?? null, purchaseCount);
  }, [user, loading]);

  const finaliseSession = useCallback(
    (u: UserData) => {
      // Write the user into the cache immediately so re-renders see the
      // authenticated state without waiting for the next /api/me probe.
      qc.setQueryData(authKeys.me(), u);
      setAuthModalOpen(false);
      // Plan 21: prefetch the post-login chunk before navigation.
      prefetchPostLoginChunks(u.role === 'admin' ? 'admin' : 'user');
      // Plan 23: invalidate per-user queries so bookings/purchases refetch
      // under the new identity.
      invalidate(qc, 'login');
      if (pendingAction) {
        const run = pendingAction;
        setPendingAction(null);
        run();
      } else {
        navigate(u.role === 'admin' ? 'admin' : 'dashboard');
      }
    },
    [qc, pendingAction]
  );

  const login = useCallback<AuthContextValue['login']>(
    async (credentials) => {
      const u = await loginRequest(credentials);
      finaliseSession(u);
      trackLogin('email');
      return u;
    },
    [finaliseSession]
  );

  const register = useCallback<AuthContextValue['register']>(
    async (payload) => {
      const u = await registerRequest(payload);
      finaliseSession(u);
      trackSignup('email');
      return u;
    },
    [finaliseSession]
  );

  const logout = useCallback<AuthContextValue['logout']>(async () => {
    try {
      await logoutRequest();
    } catch (err) {
      // The cookie may already be invalid (e.g. expired); clearing local
      // state is the whole client contract anyway.
      console.warn('Logout request failed; clearing local state regardless.', err);
    }
    qc.setQueryData(authKeys.me(), null);
    invalidate(qc, 'logout');
    navigate('');
  }, [qc]);

  const openAuthModal = useCallback((opts?: { pendingAction?: () => void }) => {
    setPendingAction(opts?.pendingAction ? () => opts.pendingAction! : null);
    setAuthModalOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => setAuthModalOpen(false), []);
  const clearPendingAction = useCallback(() => setPendingAction(null), []);

  // Deprecated setter: forward to the query cache so consumers still in
  // flight can't desync the two sources of truth.
  const setUser = useCallback(
    (u: UserData | null) => {
      qc.setQueryData(authKeys.me(), u);
    },
    [qc]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!user,
      token: null,
      loading,
      login,
      logout,
      register,
      setUser,
      openAuthModal,
      isAuthModalOpen,
      closeAuthModal,
      pendingAction,
      clearPendingAction,
    }),
    [
      user,
      loading,
      login,
      logout,
      register,
      setUser,
      openAuthModal,
      isAuthModalOpen,
      closeAuthModal,
      pendingAction,
      clearPendingAction,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
